import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ExpiryInfoBar from '../../components/ExpiryInfoBar';
import { PaymentMethods } from '../../components/payment-method/payment-card';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog';
import { useApp } from '../../context/usePortal';
import AddPaymentMethodDialog from './components/AddPaymentMethodDialog';
import ClosePopupConfirmationPopup from './components/ClosePopupConfirmationPopup';
import FailedPopup from './components/FailedPopup';
import Greeting from './components/Greeting';
import RecentActivities from './components/RecentActivities/index';
import ReferralDialog from './components/ReferralDialog';
import RewardPointsSection from './components/RewardPointsSection';
import RewardsSection from './components/RewardsSection';
import SucceedPopup from './components/SucceedPopup';
import { useAddPaymentFlow } from './hooks/useAddPaymentFlow';

const IS_EWALLET_ENABLED =
  process.env.REACT_APP_FEATURE_EWALLET_ENABLED === 'true';
const IS_REWARD_POINTS_ENABLED =
  process.env.REACT_APP_FEATURE_AYEFACE_POINTS_VISIBLE === 'true';

const Home: React.FC = () => {
  const { paymentMethods: ayePaymentMethods, user } = useApp();
  const [paymentMethods, setPaymentMethods] =
    useState<Awaited<ReturnType<typeof ayePaymentMethods>>>();
  const [succeed, setSucceed] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [open, setOpen] = useState<boolean>(false);
  const [recentlyAddedPaymentMethod] = useState<string>('');
  const [searchParams, setSearchParams] = useSearchParams();
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);

  const flow = useAddPaymentFlow({
    phoneNumber: user?.user_phone_number ?? '',
    countryCode: user?.country_code ?? '',
    userId: user?.user_id,
    name: user?.user_name ?? '',
    email: user?.user_email ?? '',
    isEWalletEnabled: IS_EWALLET_ENABLED
  });

  useEffect(() => {
    ayePaymentMethods().then((data) => {
      setPaymentMethods(data);
    });
  }, [ayePaymentMethods]);

  const handleOpen = () => {
    flow.reset();
    setOpen(true);
  };

  const onClose = () => {
    if (!isSubmitted) {
      if (showConfirmation) {
        setOpen(false);
        setShowConfirmation(false);
      } else {
        setShowConfirmation(true);
      }
    } else {
      handleConfirmClose();
    }
  };

  const handleConfirmClose = () => {
    setOpen(false);
    setShowConfirmation(false);
    setIsSubmitted(false);
  };

  const tryAgain = () => {
    setIsSubmitted(false);
    flow.reset();
  };

  useEffect(() => {
    const status = searchParams.get('status');

    if (status) {
      // Set the success/failure state
      if (status === 'success') {
        setSucceed(true);
      } else if (status === 'failed') {
        setSucceed(false);
      }

      // Open the dialog and show the result
      setIsSubmitted(true);
      setOpen(true);

      // Remove the status param from URL while keeping other params
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('status');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <>
      <div className="">
        <Greeting />

        {/* Info bar for payments which are about to expire soon or expired */}
        {paymentMethods && <ExpiryInfoBar paymentMethods={paymentMethods} />}

        <div className="flex flex-col gap-y-4 px-4 sm:px-6 md:flex-row md:justify-between lg:px-8">
          <h2 className="text-lg font-bold leading-6 text-gray-900 md:text-xl">
            Payment Methods Overview
          </h2>
          <Button
            onClick={() => handleOpen()}
            className={
              'max-w-[14rem] rounded-full bg-[#5CB891] px-2 text-primary-foreground hover:bg-[#5CB891]/80'
            }
          >
            <span className="text-sm font-semibold">
              Add New Payment Method
            </span>
          </Button>
        </div>
        <PaymentMethods />

        {IS_REWARD_POINTS_ENABLED && <RewardPointsSection />}

        <RewardsSection />

        <RecentActivities />

        <AddPaymentMethodDialog
          open={open && !isSubmitted}
          onClose={onClose}
          isEWalletEnabled={IS_EWALLET_ENABLED}
          {...flow}
        />

        {isSubmitted &&
          (succeed ? (
            <Dialog
              open={isSubmitted && succeed}
              onOpenChange={handleConfirmClose}
            >
              <DialogContent className="max-w-[25rem] rounded-3xl sm:max-w-[26rem]">
                <DialogHeader>
                  <DialogTitle className="sr-only">Payment Added</DialogTitle>
                </DialogHeader>
                <SucceedPopup
                  onClose={handleConfirmClose}
                  addedPaymentMethod={recentlyAddedPaymentMethod}
                />
              </DialogContent>
            </Dialog>
          ) : (
            <Dialog
              open={isSubmitted && !succeed}
              onOpenChange={handleConfirmClose}
            >
              <DialogContent className="max-w-[25rem] rounded-3xl sm:max-w-[26rem]">
                <DialogHeader>
                  <DialogTitle className="sr-only">Payment Failed</DialogTitle>
                </DialogHeader>
                <FailedPopup
                  onClose={handleConfirmClose}
                  tryAgain={tryAgain}
                  message={flow.errorMessage}
                />
              </DialogContent>
            </Dialog>
          ))}

        <ClosePopupConfirmationPopup
          open={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={handleConfirmClose}
        />
      </div>

      <ReferralDialog />
    </>
  );
};

export default Home;
